import { useEffect, useState, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Bell,
  Search,
  Settings,
  LogOut,
  User,
  ShoppingBag,
  Home,
  Menu,
  MapPin,
  CreditCard,
  ScanEye,
  HelpCircle,
  Package,
  X,
} from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { useSidebar } from "@/components/ui/sidebar";
import { useAuth } from "@/context/Auth";
import { getUserOrders } from "@/utils/helper/order";

// Dashboard navigation items for search (constant outside component)
const navigationItems = [
  { title: "Dashboard", path: "/dashboard/overview", icon: Home, category: "Page" },
  { title: "Orders", path: "/dashboard/orders", icon: ShoppingBag, category: "Page" },
  { title: "Addresses", path: "/dashboard/addresses", icon: MapPin, category: "Page" },
  { title: "Skin Analysis", path: "/dashboard/analysis/skin", icon: ScanEye, category: "Page" },
  { title: "Transactions", path: "/dashboard/transactions", icon: CreditCard, category: "Page" },
  { title: "Settings", path: "/dashboard/settings", icon: Settings, category: "Page" },
  { title: "Help & Support", path: "/dashboard/support", icon: HelpCircle, category: "Page" },
];

export default function DashboardHeader() {
  const { toggleSidebar } = useSidebar();
  const { getUserDetails, logout } = useAuth();
  const [userDetails, setUserDetails] = useState(null);
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [orders, setOrders] = useState([]);
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const searchRef = useRef(null);
  const resultsRef = useRef(null);

  useEffect(() => {
    const loadUser = async () => {
      const details = await getUserDetails();
      setUserDetails(details);
    };
    loadUser();
  }, [getUserDetails]);

  // Fetch orders on component mount
  useEffect(() => {
    const fetchOrders = async () => {
      try {
        const userOrders = await getUserOrders();
        setOrders(userOrders || []);
      } catch (error) {
        console.error("Failed to fetch orders for search:", error);
        // Set empty array on error to prevent search from breaking
        setOrders([]);
      }
    };
    fetchOrders();
  }, []);

  // Search functionality
  useEffect(() => {
    if (!searchQuery.trim()) {
      setSearchResults([]);
      return;
    }

    const query = searchQuery.toLowerCase().trim();
    const results = [];
    const addedOrderIds = new Set(); // Track added orders to avoid duplicates

    // Search navigation items
    navigationItems.forEach((item) => {
      if (item.title.toLowerCase().includes(query)) {
        results.push({
          type: "navigation",
          title: item.title,
          path: item.path,
          icon: item.icon,
          category: item.category,
        });
      }
    });

    // Search orders
    orders.forEach((order) => {
      const orderId = order.id || order._id || order.orderId || "";
      if (!orderId) return; // Skip orders without ID
      
      const orderIdLower = orderId.toLowerCase();
      const status = (order.status || "").toLowerCase();
      const trackingNumber = (order.trackingNumber || "").toLowerCase();
      const hasBeenAdded = addedOrderIds.has(orderId);

      // Check if order matches search query
      const matchesOrderId = orderIdLower.includes(query);
      const matchesStatus = status.includes(query);
      const matchesTracking = trackingNumber && trackingNumber.includes(query);
      
      // Check product names
      let matchesProduct = false;
      let matchedProductName = "";
      
      if (order.products && Array.isArray(order.products)) {
        for (const product of order.products) {
          const productName = (product.name || product.productName || "").toLowerCase();
          if (productName.includes(query)) {
            matchesProduct = true;
            matchedProductName = product.name || product.productName;
            break;
          }
        }
      }
      
      // Check items array
      if (!matchesProduct && order.items && Array.isArray(order.items)) {
        for (const item of order.items) {
          const productName = (item.product?.name || item.name || "").toLowerCase();
          if (productName.includes(query)) {
            matchesProduct = true;
            matchedProductName = item.product?.name || item.name;
            break;
          }
        }
      }

      // Add order if it matches any criteria and hasn't been added yet
      if (!hasBeenAdded && (matchesOrderId || matchesStatus || matchesTracking || matchesProduct)) {
        addedOrderIds.add(orderId);
        
        // Determine path and subtitle based on match type
        let path = `/dashboard/orders/${orderId}`;
        let subtitle = `${order.status || "Unknown"} • ${new Date(order.createdAt || order.date || Date.now()).toLocaleDateString()}`;
        
        if (matchesTracking && order.trackingNumber) {
          path = `/dashboard/orders/track/${order.trackingNumber}`;
          subtitle = `Tracking: ${order.trackingNumber}`;
        } else if (matchesProduct && matchedProductName) {
          subtitle = `Contains: ${matchedProductName}`;
        }

        results.push({
          type: "order",
          title: `Order ${orderId}`,
          path,
          icon: Package,
          category: "Order",
          subtitle,
          order,
        });
      }
    });

    // Limit results to 8 items
    setSearchResults(results.slice(0, 8));
  }, [searchQuery, orders]);

  // Close search results when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        searchRef.current &&
        !searchRef.current.contains(event.target) &&
        resultsRef.current &&
        !resultsRef.current.contains(event.target)
      ) {
        setIsSearchFocused(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleSearchResultClick = (path) => {
    navigate(path);
    setSearchQuery("");
    setIsSearchFocused(false);
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === "Escape") {
      setSearchQuery("");
      setIsSearchFocused(false);
    } else if (e.key === "Enter" && searchResults.length > 0) {
      e.preventDefault();
      handleSearchResultClick(searchResults[0].path);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate("/auth/login"); // Redirect to login page after logout
    } catch (error) {
      console.error("Logout failed", error);
    }
  };

  // Get user initials from firstName and lastName
  const getUserInitials = () => {
    if (!userDetails) return "U";
    const first = userDetails.firstName?.charAt(0)?.toUpperCase() || "";
    const last = userDetails.lastName?.charAt(0)?.toUpperCase() || "";
    return first && last ? `${first}${last}` : first || last || "U";
  };

  return (
    <header className="sticky top-0 z-50 flex h-16 shrink-0 items-center gap-4 border-b bg-white px-4 md:px-6">
      {/* Mobile Sidebar Trigger */}
      <Button
        variant="ghost"
        size="icon"
        className="md:hidden text-primary"
        onClick={toggleSidebar} // Toggle sidebar on click
        aria-label="Toggle sidebar"
      >
        <Menu className="w-5 h-5" />
      </Button>

      {/* Desktop Logo/Dashboard Title */}
      <Link
        to="/dashboard"
        className="hidden md:flex items-center gap-2 text-lg font-semibold text-primary"
      >
        <Home className="w-5 h-5 text-secondary" />
        <span>Dashboard</span>
      </Link>

      {/* Search Bar */}
      <div className="flex-1 relative max-w-md ml-auto hidden md:block" ref={searchRef}>
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 z-10" />
        <Input
          type="search"
          placeholder="Search dashboard..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onFocus={() => setIsSearchFocused(true)}
          onKeyDown={handleSearchKeyDown}
          className="w-full rounded-lg bg-gray-100 pl-9 focus:border-secondary focus:ring-secondary"
        />
        {searchQuery && (
          <button
            onClick={() => {
              setSearchQuery("");
              setSearchResults([]);
            }}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
          >
            <X className="h-4 w-4" />
          </button>
        )}

        {/* Search Results Dropdown */}
        {isSearchFocused && searchResults.length > 0 && (
          <div
            ref={resultsRef}
            className="absolute top-full left-0 right-0 mt-2 bg-white border rounded-lg shadow-lg z-50 max-h-96 overflow-y-auto"
          >
            <div className="p-2">
              {searchResults.map((result, index) => {
                const Icon = result.icon;
                return (
                  <button
                    key={index}
                    onClick={() => handleSearchResultClick(result.path)}
                    className="w-full flex items-center gap-3 px-3 py-2 rounded-md hover:bg-gray-100 transition-colors text-left"
                  >
                    <Icon className="h-5 w-5 text-gray-500 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {result.title}
                      </p>
                      {result.subtitle && (
                        <p className="text-xs text-gray-500 truncate">
                          {result.subtitle}
                        </p>
                      )}
                    </div>
                    <span className="text-xs text-gray-400 flex-shrink-0">
                      {result.category}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* No Results Message */}
        {isSearchFocused && searchQuery.trim() && searchResults.length === 0 && (
          <div
            ref={resultsRef}
            className="absolute top-full left-0 right-0 mt-2 bg-white border rounded-lg shadow-lg z-50 p-4"
          >
            <p className="text-sm text-gray-500 text-center">
              No results found for "{searchQuery}"
            </p>
          </div>
        )}
      </div>

      {/* Right-aligned actions */}
      <div className="flex items-center gap-4 ml-auto md:ml-0">
        <Button
          variant="ghost"
          size="icon"
          className="text-primary hover:text-secondary"
        >
          <Bell className="w-5 h-5" />
          <span className="sr-only">Notifications</span>
        </Button>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="relative h-8 w-8 rounded-full">
              <Avatar className="h-8 w-8">
                <AvatarImage
                  src="/placeholder.svg?height=32&width=32"
                  alt="User Avatar"
                />
                <AvatarFallback className="bg-secondary text-primary">
                  {getUserInitials()}
                </AvatarFallback>
              </Avatar>
              <span className="sr-only">User menu</span>
            </Button>
          </DropdownMenuTrigger>

          <DropdownMenuContent className="w-56" align="end" forceMount>
            <DropdownMenuLabel className="font-normal">
              <div className="flex flex-col space-y-1">
                <p className="text-sm font-medium leading-none">
                  {userDetails?.firstName} {userDetails?.lastName}
                </p>
                <p className="text-xs leading-none text-muted-foreground">
                  {userDetails?.email}
                </p>
              </div>
            </DropdownMenuLabel>

            <DropdownMenuSeparator />

            <DropdownMenuGroup>
              <DropdownMenuItem asChild>
                <Link to="/dashboard/profile" className="flex items-center">
                  <User className="mr-2 h-4 w-4" />
                  <span>Profile</span>
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to="/dashboard/orders" className="flex items-center">
                  <ShoppingBag className="mr-2 h-4 w-4" />
                  <span>Orders</span>
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to="/dashboard/settings" className="flex items-center">
                  <Settings className="mr-2 h-4 w-4" />
                  <span>Settings</span>
                </Link>
              </DropdownMenuItem>
            </DropdownMenuGroup>

            <DropdownMenuSeparator />

            {/* Logout with AlertDialog Trigger outside DropdownMenuItem */}
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <button
                  className="w-full text-left px-2 py-1.5 text-sm hover:bg-muted flex items-center gap-2"
                  onClick={(e) => e.stopPropagation()} // prevent DropdownMenu from closing
                >
                  <LogOut className="h-4 w-4" />
                  <span>Log out</span>
                </button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Are you sure?</AlertDialogTitle>
                  <AlertDialogDescription>
                    You will be logged out and redirected to the login page.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={handleLogout}>
                    Log Out
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
